import Experience from "@folio-2026/experience"
import gsap from "gsap"
import { InteractionManager } from "three.interactive"
import {
  color,
  float,
  Fn,
  fract,
  length,
  mix,
  positionGeometry,
  smoothstep,
  texture,
  uniform,
  uv,
  vec2,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"

import Text, { type MSDFFont } from "./Text"

type SmallButtonProps = {
  name: string
  scene: THREE.Scene
  element: HTMLElement
  shouldUpdate?: boolean
  interactionManager: InteractionManager
  perlinTexture?: THREE.Texture
}

export default class SmallButton {
  experience: Experience
  globalResources: Experience["globalResources"]
  sizes: Experience["sizes"]
  pointer: Experience["pointer"]
  name: string
  scene: THREE.Scene
  element: HTMLElement
  bounds!: DOMRect
  cachedRect!: DOMRect // Cached bounding rect to avoid repeated getBoundingClientRect calls
  y!: number
  mesh!: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicNodeMaterial>
  proxyMesh!: THREE.Mesh
  shouldUpdate: boolean = true
  interactionManager: InteractionManager
  isHovered = false // Track hover state for mouse circle updates
  colors: {
    default: ReturnType<typeof color>
  }
  id: string
  group: THREE.Group
  cornersMesh?: THREE.InstancedMesh
  cornersMaterial?: THREE.MeshBasicNodeMaterial
  borderMesh?: THREE.InstancedMesh
  borderMaterial?: THREE.MeshBasicNodeMaterial
  borderOpacity: ReturnType<typeof uniform>
  borderThickness = 2 // Thickness of border rectangles in pixels
  overallOpacity: THREE.UniformNode<number>
  mouseCircleUniform: THREE.UniformNode<THREE.Vector2>
  mouseCircleRadius = 150 // Radius in pixels
  mouseCircleOpacity = 0.5 // Opacity of the circle
  bgMesh?: THREE.Mesh
  bgMaterial?: THREE.MeshBasicNodeMaterial
  textMesh?: Text
  iconTexture?: THREE.CanvasTexture
  iconMesh?: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicNodeMaterial>
  iconMaterial?: THREE.MeshBasicNodeMaterial
  perlinTexture?: THREE.Texture
  cornerSquareSize = 2
  /** Length of each leg of the L-shaped corner (in pixels). Use > cornerSquareSize to make the L clearly visible. */
  cornerLegLength = 6
  gridSquareSize = 20
  /** Default raster resolution for SVG icon texture (pixels). */
  static readonly DEFAULT_ICON_RASTER_SIZE = 32
  /** Default quad size in pixels for icon mesh. */
  static readonly DEFAULT_ICON_QUAD_SIZE = 16
  emissiveIntensity: {
    corners: ReturnType<typeof uniform>
    bg: ReturnType<typeof uniform>
  }

  constructor({
    name,
    scene,
    element,
    shouldUpdate = true,
    interactionManager,
    perlinTexture,
  }: SmallButtonProps) {
    this.experience = Experience.getInstance()
    this.globalResources = this.experience.globalResources
    this.sizes = this.experience.sizes
    this.pointer = this.experience.pointer

    this.name = name
    this.scene = scene
    this.element = element
    this.id = element.id

    this.colors = {
      default: color("#ffffff"),
    }

    this.emissiveIntensity = {
      corners: uniform(0.1),
      bg: uniform(0),
    }

    this.mouseCircleUniform = uniform(vec2(0.5))
    this.overallOpacity = uniform(0)
    this.borderOpacity = uniform(0.05)
    this.perlinTexture = perlinTexture

    this.group = new THREE.Group()

    this.interactionManager = interactionManager

    this.createBounds()

    this.createMesh()
    this.createProxyMesh()

    this.eventListeners()

    /**
     * ?Calling update() before setting shouldUpdate to set the mesh on the initial position.
     */
    this.update()
    this.shouldUpdate = shouldUpdate
  }

  eventListeners() {
    this.interactionManager.add(this.proxyMesh)

    // Store bound method references for proper cleanup
    this.proxyMesh.userData.hoverHandler = this.onMouseOver.bind(this)
    this.proxyMesh.userData.outHandler = this.onMouseOut.bind(this)
    this.proxyMesh.userData.clickHandler = this.onClick.bind(this)

    this.proxyMesh.addEventListener(
      "mouseover" as any,
      this.proxyMesh.userData.hoverHandler,
    )

    this.proxyMesh.addEventListener(
      "mouseout" as any,
      this.proxyMesh.userData.outHandler,
    )

    this.proxyMesh.addEventListener(
      "click" as any,
      this.proxyMesh.userData.clickHandler,
    )

    // Detect if device supports touch (mobile)
    const isTouchDevice =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-expect-error - msMaxTouchPoints is a legacy IE property
      navigator.msMaxTouchPoints > 0

    // Add cursor pointer handlers for interactive buttons (clickable or hoverable)
    const clickEvent = this.element.getAttribute("data-click-event")
    const hoverEvent = this.element.getAttribute("data-hover-event")
    if ((clickEvent || hoverEvent) && !isTouchDevice) {
      const cursorOverHandler = () => {
        if (
          this.proxyMesh.visible &&
          this.experience.renderer.instance.domElement
        ) {
          this.experience.renderer.instance.domElement.style.cursor = "pointer"
        }
      }

      const cursorOutHandler = () => {
        if (this.experience.renderer.instance.domElement) {
          this.experience.renderer.instance.domElement.style.cursor = "default"
        }
      }

      this.proxyMesh.addEventListener("mouseover" as any, cursorOverHandler)
      this.proxyMesh.addEventListener("mouseout" as any, cursorOutHandler)
      // Store handlers for potential cleanup
      this.proxyMesh.userData.cursorOverHandler = cursorOverHandler
      this.proxyMesh.userData.cursorOutHandler = cursorOutHandler
    }
  }

  onMouseOver(event: MouseEvent) {
    const { target } = event

    this.isHovered = true
    this.updateCachedRect()

    const hoverEvent = this.element.getAttribute("data-hover-event")

    if (hoverEvent && this.proxyMesh.visible) {
      // Animate the uniform directly (like Text.ts does)
      gsap.killTweensOf(this.borderOpacity, "value")
      gsap.to(this.borderOpacity, {
        value: 0.5,
        duration: 1,
        ease: "power2.out",
        overwrite: "auto",
      })

      this.experience.trigger(hoverEvent, [
        {
          target,
          htmlElement: this.element,
        },
      ])
    }
  }

  onMouseOut(event: MouseEvent) {
    const { target } = event

    this.isHovered = false

    // Reset circle position when mouse leaves
    if (this.mouseCircleUniform) {
      // Reset to center
      this.mouseCircleUniform.value.x = 0.5
      this.mouseCircleUniform.value.y = 0.5
    }

    const outEvent = this.element.getAttribute("data-out-event")

    if (outEvent && this.proxyMesh.visible) {
      // Animate the uniform directly (like Text.ts does)
      gsap.killTweensOf(this.borderOpacity, "value")
      gsap.to(this.borderOpacity, {
        value: 0.05,
        duration: 0.5,
        ease: "power2.out",
        overwrite: "auto",
      })

      this.experience.trigger(outEvent, [
        {
          target,
          htmlElement: this.element,
        },
      ])
    }
  }

  updateCachedRect() {
    // Cache the bounding rect to avoid repeated getBoundingClientRect calls
    this.cachedRect = this.element.getBoundingClientRect()
  }

  updateMouseCircle() {
    if (
      !this.mouseCircleUniform ||
      !this.bgMesh ||
      !this.bounds ||
      !this.isHovered
    )
      return

    // Validate bounds dimensions
    if (this.bounds.width <= 0 || this.bounds.height <= 0) {
      return
    }

    // Use cached rect if available, otherwise update it
    if (!this.cachedRect) {
      this.updateCachedRect()
    }

    // Use pointer pixel coordinates directly
    const screenX = this.pointer.x
    const screenY = this.pointer.y

    // Convert screen coordinates to button-relative coordinates
    const buttonX = screenX - this.cachedRect.left
    const buttonY = screenY - this.cachedRect.top

    // Convert button-relative coordinates to UV space (0 to 1)
    this.mouseCircleUniform.value.x = buttonX / this.bounds.width
    this.mouseCircleUniform.value.y = buttonY / this.bounds.height
  }

  onClick(
    event:
      | MouseEvent
      | {
          target?: THREE.Object3D
          originalEvent?: MouseEvent
          stopPropagation?: () => void
        }
      | undefined,
  ) {
    event?.stopPropagation?.()

    const target = event && "target" in event ? event.target : this.proxyMesh
    const clickEvent = this.element.getAttribute("data-click-event")

    if (clickEvent && this.proxyMesh.visible) {
      this.experience.trigger(clickEvent, [
        {
          target,
          htmlElement: this.element,
        },
      ])
    }
  }

  createMesh() {
    this.createCornersMesh()
    this.createBorderMesh()
    this.createBGMesh()
    this.createTextMesh()
  }

  private createBorderMesh() {
    const borderGeometry = new THREE.PlaneGeometry(1, 1)

    const material = new THREE.MeshBasicNodeMaterial({
      colorNode: vec3(0.1),
      side: THREE.DoubleSide,
    })

    material.opacityNode = this.borderOpacity.mul(this.overallOpacity)
    material.transparent = true

    this.borderMaterial = material

    // Create InstancedMesh with 4 instances (top, bottom, left, right)
    const instanceCount = 4
    this.borderMesh = new THREE.InstancedMesh(
      borderGeometry,
      material,
      instanceCount,
    )

    this.positionBorderMesh()

    this.group.add(this.borderMesh)
  }

  private positionBorderMesh() {
    if (!this.borderMesh) {
      return
    }

    const instanceCount = 4

    // Calculate positions and scales for each border
    const halfWidth = this.bounds.width / 2
    const halfHeight = this.bounds.height / 2
    const borderThickness = this.borderThickness

    const borders = [
      {
        // Top border
        x: 0,
        y: halfHeight - borderThickness / 2,
        z: 0,
        scaleX: this.bounds.width,
        scaleY: borderThickness,
        rotation: 0,
      },
      {
        // Bottom border
        x: 0,
        y: -halfHeight + borderThickness / 2,
        z: 0,
        scaleX: this.bounds.width,
        scaleY: borderThickness,
        rotation: 0,
      },
      {
        // Left border
        x: -halfWidth + borderThickness / 2,
        y: 0,
        z: 0,
        scaleX: borderThickness,
        scaleY: this.bounds.height,
        rotation: 0,
      },
      {
        // Right border
        x: halfWidth - borderThickness / 2,
        y: 0,
        z: 0,
        scaleX: borderThickness,
        scaleY: this.bounds.height,
        rotation: 0,
      },
    ]

    const matrix = new THREE.Matrix4()

    for (let i = 0; i < instanceCount; i++) {
      const border = borders[i]
      matrix.makeScale(border.scaleX, border.scaleY, 1)
      matrix.setPosition(border.x, border.y, border.z)
      this.borderMesh.setMatrixAt(i, matrix)
    }
  }

  private createCornersMesh() {
    const geometry = new THREE.PlaneGeometry(
      this.cornerSquareSize,
      this.cornerSquareSize,
    )

    const material = new THREE.MeshBasicNodeMaterial({
      colorNode: this.colors.default,
      side: THREE.DoubleSide,
      transparent: true,
    })

    ;(material as unknown as THREE.MeshPhysicalNodeMaterial).emissiveNode =
      this.colors.default.mul(this.emissiveIntensity.corners)

    material.opacityNode = float(1).mul(this.overallOpacity)
    this.cornersMaterial = material

    // Two instances per corner (horizontal + vertical leg of L), 4 corners = 8 instances
    const instanceCount = 8
    this.cornersMesh = new THREE.InstancedMesh(
      geometry,
      material,
      instanceCount,
    )

    this.positionCornersMesh()

    this.group.add(this.cornersMesh)
  }

  private positionCornersMesh() {
    if (!this.cornersMesh) {
      return
    }

    const halfWidth = this.bounds.width / 2
    const halfHeight = this.bounds.height / 2
    const s = this.cornerSquareSize
    const L = this.cornerLegLength
    const quarter = s / 4
    const legHalf = L / 2
    // Scale so leg length = L: base geometry is s×s, so scale (L/s, 0.5) gives width L, height s/2
    const horizontalScaleX = L / s
    const verticalScaleY = L / s

    // Two legs per corner forming an L: horizontal bar, vertical bar. Each leg length = cornerLegLength.
    type Leg = { x: number; y: number; scaleX: number; scaleY: number }
    const legs: Leg[] = [
      // Top-left: horizontal (right), vertical (down)
      {
        x: -halfWidth + legHalf,
        y: halfHeight - quarter,
        scaleX: horizontalScaleX,
        scaleY: 0.5,
      },
      {
        x: -halfWidth + quarter,
        y: halfHeight - legHalf,
        scaleX: 0.5,
        scaleY: verticalScaleY,
      },
      // Top-right: horizontal (left), vertical (down)
      {
        x: halfWidth - legHalf,
        y: halfHeight - quarter,
        scaleX: horizontalScaleX,
        scaleY: 0.5,
      },
      {
        x: halfWidth - quarter,
        y: halfHeight - legHalf,
        scaleX: 0.5,
        scaleY: verticalScaleY,
      },
      // Bottom-left: horizontal (right), vertical (up)
      {
        x: -halfWidth + legHalf,
        y: -halfHeight + quarter,
        scaleX: horizontalScaleX,
        scaleY: 0.5,
      },
      {
        x: -halfWidth + quarter,
        y: -halfHeight + legHalf,
        scaleX: 0.5,
        scaleY: verticalScaleY,
      },
      // Bottom-right: horizontal (left), vertical (up)
      {
        x: halfWidth - legHalf,
        y: -halfHeight + quarter,
        scaleX: horizontalScaleX,
        scaleY: 0.5,
      },
      {
        x: halfWidth - quarter,
        y: -halfHeight + legHalf,
        scaleX: 0.5,
        scaleY: verticalScaleY,
      },
    ]

    const matrix = new THREE.Matrix4()
    const position = new THREE.Vector3()
    const scale = new THREE.Vector3()

    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i]
      position.set(leg.x, leg.y, 0)
      scale.set(leg.scaleX, leg.scaleY, 1)
      matrix.compose(position, new THREE.Quaternion(), scale)
      this.cornersMesh.setMatrixAt(i, matrix)
    }

    this.cornersMesh.instanceMatrix.needsUpdate = true
  }

  private createBGMesh() {
    const geometry = new THREE.PlaneGeometry(
      this.bounds.width,
      this.bounds.height,
    )

    const material = new THREE.MeshBasicNodeMaterial({
      colorNode: this.colors.default,
      side: THREE.DoubleSide,
      transparent: true,
    })

    ;(material as unknown as THREE.MeshPhysicalNodeMaterial).emissiveNode =
      this.colors.default.mul(this.emissiveIntensity.bg)

    const gridMask = Fn(() => {
      // Create grid pattern (same logic as Button class)
      const gridSizeUV = float(this.gridSquareSize).div(
        float(Math.max(this.bounds.width, this.bounds.height)),
      )
      // For PlaneGeometry, use UVs directly without aspect correction for now
      // to debug the grid pattern
      const gridCell = uv().div(gridSizeUV)
      const gridCellLocal = fract(gridCell) // Position within each grid cell (0-1)

      // Create grid lines (borders) using smoothstep for smooth edges
      // gridCellLocal.x is 0 at edges (lines) and 1 in middle (squares)
      // We need to detect when x is near 0 OR near 1
      const lineWidth = float(0.1) // Width of grid lines in UV space (increased for visibility)
      const oneMinusLineWidth = lineWidth.oneMinus()

      // For vertical lines (x-axis):
      // Left edge: when x is near 0, (1 - x) is near 1
      // Right edge: when x is near 1, we need to detect it separately
      // Use smoothstep for smoother transitions, but make the transition sharper
      const nearLeft = smoothstep(0, lineWidth, gridCellLocal.x).oneMinus()
      const nearRight = smoothstep(oneMinusLineWidth, 1, gridCellLocal.x) // 1 when x is near 1
      // Combine: use max to get 1 when either edge is detected
      const verticalLine = nearLeft.max(nearRight)

      // For horizontal lines (y-axis): same logic
      const nearTop = smoothstep(0, lineWidth, gridCellLocal.y).oneMinus() // 1 at top edge, 0 when y > lineWidth
      const nearBottom = smoothstep(oneMinusLineWidth, 1, gridCellLocal.y) // 1 when y is near 1
      const horizontalLine = nearTop.max(nearBottom)

      // Combine horizontal and vertical lines to create square borders
      return horizontalLine.add(verticalLine).min(1).mul(0.003)
    })()

    const scaleUV = Fn(([uv, scale]: [any, any]) => {
      return uv.toVar().sub(vec2(0.5)).mul(scale).add(vec2(0.5))
    })
    const aspect = uniform(vec2(1, this.sizes.width / this.sizes.height))
    //
    // Create mouse circle effect
    const maxDimension = float(Math.max(this.bounds.width, this.bounds.height))
    const circleRadiusUV = float(this.mouseCircleRadius).div(maxDimension) // Convert radius to UV space
    // Convert positionGeometry to UV space (0-1)
    // Geometry is centered at (0,0) and scaled to bounds, so we convert to UV
    const halfWidth = float(this.bounds.width / 2)
    const halfHeight = float(this.bounds.height / 2)
    const uvCoords = vec2(
      positionGeometry.x.div(halfWidth).add(float(1)).div(float(2)),
      positionGeometry.y.div(halfHeight).add(float(1)).div(float(2)),
    )
    const corrUVCoords = scaleUV(uvCoords, aspect)
    const distanceFromMouse = length(corrUVCoords.sub(this.mouseCircleUniform))

    // Create circular mask with smooth edges
    // smoothstep(outer, inner, distance) returns 0 when distance < outer, 1 when distance > inner
    // We want 1 inside the circle, 0 outside, so we invert it with oneMinus
    const fadeWidth = circleRadiusUV.mul(float(0.6)) // Fade width for smooth edges
    const outerRadius = circleRadiusUV // Outer edge
    const innerRadius = circleRadiusUV.sub(fadeWidth) // Inner edge (softer)
    const circleMask = smoothstep(outerRadius, innerRadius, distanceFromMouse)
      .pow(2)
      .mul(3)

    // Use circle mask to control opacity
    // Inside circle: high opacity, Outside circle: low opacity
    const baseOpacity = float(0)
    const circleOpacity = float(this.mouseCircleOpacity) // High opacity inside circle
    const circleOpacityValue = mix(baseOpacity, circleOpacity, circleMask)

    const finalOpacity = gridMask
      .mul(circleOpacityValue)
      .mul(this.overallOpacity)

    material.opacityNode = finalOpacity
    material.transparent = true

    this.bgMaterial = material

    // Create mesh
    this.bgMesh = new THREE.Mesh(geometry, material)
    this.bgMesh.rotation.x = Math.PI
    this.bgMesh.position.set(0, 0, 0)

    this.group.add(this.bgMesh)
    this.scene.add(this.group)
  }

  /**
   * Rasterizes an SVG element to a canvas and returns a THREE.CanvasTexture.
   * SmallButton owns the returned texture and must dispose it.
   */
  private rasterizeSvgToTexture(
    svg: SVGElement,
    size: number,
  ): Promise<THREE.CanvasTexture> {
    return new Promise((resolve, reject) => {
      const clone = svg.cloneNode(true) as SVGElement
      if (!clone.hasAttribute("width") && !clone.hasAttribute("viewBox")) {
        clone.setAttribute("width", String(size))
        clone.setAttribute("height", String(size))
      } else if (
        clone.hasAttribute("viewBox") &&
        !clone.hasAttribute("width")
      ) {
        clone.setAttribute("width", String(size))
        clone.setAttribute("height", String(size))
      }
      const svgString = new XMLSerializer().serializeToString(clone)
      const blob = new Blob([svgString], { type: "image/svg+xml" })
      const url = URL.createObjectURL(blob)
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(url)
        const canvas = document.createElement("canvas")
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext("2d")
        if (!ctx) {
          reject(new Error("Could not get canvas 2d context"))
          return
        }
        ctx.drawImage(img, 0, 0, size, size)
        const tex = new THREE.CanvasTexture(canvas)
        tex.colorSpace = THREE.SRGBColorSpace
        tex.needsUpdate = true
        resolve(tex)
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("Failed to load SVG as image"))
      }
      img.src = url
    })
  }

  private createIconMesh(tex: THREE.Texture, quadSizePx: number): void {
    const geometry = new THREE.PlaneGeometry(quadSizePx, quadSizePx)
    const material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      side: THREE.DoubleSide,
    })
    // Flip V so canvas (Y-down) matches view after rotation.x = Math.PI
    const uvFlipped = Fn(() => vec2(uv().x, uv().y.oneMinus()))
    material.colorNode = texture(tex, uvFlipped()).rgb
    material.opacityNode = texture(tex, uvFlipped()).a.mul(this.overallOpacity)
    this.iconMaterial = material
    this.iconMesh = new THREE.Mesh(geometry, material)
    this.iconMesh.rotation.x = Math.PI
    this.iconMesh.position.set(0, 0, 0) // 5px up from previous -5
    this.group.add(this.iconMesh)
  }

  private positionIconMesh(): void {
    if (!this.iconMesh) return
    this.iconMesh.position.set(0, 0, 0)
  }

  private async createTextMesh() {
    // Icon mode: data-icon + SVG child → rasterize and show icon quad instead of text
    if (this.element.hasAttribute("data-icon")) {
      const svgEl = this.element.querySelector("svg")
      if (svgEl) {
        const rasterSizeAttr = this.element.getAttribute("data-icon-size")
        const rasterSize = rasterSizeAttr
          ? parseInt(rasterSizeAttr, 10)
          : SmallButton.DEFAULT_ICON_RASTER_SIZE
        const quadSize = SmallButton.DEFAULT_ICON_QUAD_SIZE
        const tex = await this.rasterizeSvgToTexture(svgEl, rasterSize)
        this.iconTexture = tex as THREE.CanvasTexture
        this.createIconMesh(tex, quadSize)
        return
      }
    }

    if (!this.perlinTexture) {
      return
    }

    const atlas = this.globalResources.atlasUbuntuMonoBold as THREE.Texture
    const font = this.globalResources.bmfontUbuntuMonoBold as MSDFFont

    this.textMesh = new Text({
      name: `${this.name}-smallbutton`,
      element: this.element,
      scene: this.group as unknown as THREE.Scene,
      atlas,
      font,
      shouldUpdate: false,
      preventInitialUpdate: true,
      uniforms: {
        color: uniform(color("#ffffff")),
        mouseCoords: uniform(vec2(0)),
        emissiveIntensity: uniform(0.01),
        scale: 1,
      },
    })

    this.textMesh.leave()
  }

  private positionTextMesh() {
    if (!this.textMesh?.mesh || !this.textMesh.bounds) {
      return
    }

    const textMeshWorldX =
      this.textMesh.bounds.left - this.experience.sizes.width / 2
    const textMeshWorldY =
      -this.textMesh.y +
      this.experience.scrollInfo.animatedScroll +
      this.experience.sizes.height / 2 -
      this.textMesh.bounds.height

    const textCenterWorldX = textMeshWorldX
    const textCenterWorldY = textMeshWorldY + this.textMesh.bounds.height / 2

    // Button center in world space (where the group is positioned)
    const buttonCenterWorldX =
      this.bounds.left + this.bounds.width / 2 - this.experience.sizes.width / 2
    const buttonCenterWorldY =
      -this.y +
      this.experience.scrollInfo.animatedScroll +
      this.experience.sizes.height / 2 -
      this.bounds.height / 2

    // Position mesh relative to group so text center aligns with button center
    const meshX = textCenterWorldX - buttonCenterWorldX
    const meshY = textCenterWorldY - buttonCenterWorldY - 5

    this.textMesh.mesh.position.set(meshX, meshY, 0)
  }

  createProxyMesh() {
    const geometry = new THREE.PlaneGeometry(
      this.bounds.width,
      this.bounds.height,
    )
    const material = new THREE.MeshBasicNodeMaterial()
    material.colorNode = color("#0fd030")
    material.transparent = true
    material.opacityNode = float(0)
    this.proxyMesh = new THREE.Mesh(geometry, material)
    this.proxyMesh.visible = false

    this.scene.add(this.proxyMesh)
  }

  createBounds() {
    this.bounds = this.element.getBoundingClientRect()
    this.updateCachedRect()
    this.y = this.bounds.top + this.experience.scrollInfo.actualScroll
  }

  /**
   * Shows the main and proxy meshes by making them visible.
   */
  reveal() {
    if (!this.proxyMesh) {
      return
    }

    setTimeout(() => {
      // Ensure bounds are up-to-date before positioning content
      // This handles the case where resize() was called before reveal()
      this.createBounds()
      if (this.iconMesh) {
        this.positionIconMesh()
      } else {
        // Resize text mesh to update its bounds with current screen resolution
        this.textMesh?.resize()
        this.positionTextMesh()
        this.textMesh?.reveal()
      }
    }, 500)

    gsap.to(this.overallOpacity, {
      value: 1,
      duration: 1,
      ease: "power2.out",
    })

    this.proxyMesh.visible = true
  }

  resetToRevealStart() {
    if (!this.proxyMesh) {
      return
    }

    // Leave the text (icon has no leave animation)
    this.textMesh?.resetToRevealStart()

    // Kill any tweens on the overall opacity
    gsap.killTweensOf(this.overallOpacity)

    this.overallOpacity.value = 0
    this.proxyMesh.visible = false
  }

  /**
   * Hides the main and proxy meshes by making them invisible.
   */
  leave() {
    if (!this.proxyMesh) {
      return
    }

    // Leave the text (icon has no leave animation)
    this.textMesh?.leave()

    gsap.to(this.overallOpacity, {
      value: 0,
      duration: 0.5,
      ease: "power2.out",
    })

    this.proxyMesh.visible = false
  }

  dispose() {
    // Remove event listeners
    this.interactionManager.remove(this.proxyMesh)
    this.proxyMesh.removeEventListener(
      "mouseover" as any,
      this.proxyMesh.userData.hoverHandler,
    )
    this.proxyMesh.removeEventListener(
      "mouseout" as any,
      this.proxyMesh.userData.outHandler,
    )
    this.proxyMesh.removeEventListener(
      "click" as any,
      this.proxyMesh.userData.clickHandler,
    )

    // Remove cursor handlers
    if (this.proxyMesh.userData.cursorOverHandler) {
      this.proxyMesh.removeEventListener(
        "mouseover" as any,
        this.proxyMesh.userData.cursorOverHandler,
      )
      delete this.proxyMesh.userData.cursorOverHandler
    }

    if (this.proxyMesh.userData.cursorOutHandler) {
      this.proxyMesh.removeEventListener(
        "mouseout" as any,
        this.proxyMesh.userData.cursorOutHandler,
      )
      delete this.proxyMesh.userData.cursorOutHandler
    }

    // Remove meshes from scene before disposing
    if (this.proxyMesh) {
      this.scene.remove(this.proxyMesh)
      this.proxyMesh.geometry.dispose()
      if (this.proxyMesh.material instanceof THREE.MeshBasicNodeMaterial) {
        this.proxyMesh.material.dispose()
      }
      this.proxyMesh = undefined!
    }
    if (this.textMesh) {
      this.textMesh.remove()
      this.textMesh = undefined!
    }
    if (this.iconMesh) {
      this.group.remove(this.iconMesh)
      this.iconMesh.geometry.dispose()
      this.iconMesh = undefined!
    }
    if (this.iconMaterial) {
      this.iconMaterial.dispose()
      this.iconMaterial = undefined!
    }
    if (this.iconTexture) {
      this.iconTexture.dispose()
      this.iconTexture = undefined!
    }
    if (this.cornersMesh) {
      this.cornersMesh.geometry.dispose()
      if (this.cornersMesh.material instanceof THREE.MeshBasicNodeMaterial) {
        this.cornersMesh.material.dispose()
      }
      this.cornersMesh = undefined!
    }
    if (this.borderMesh) {
      this.borderMesh.geometry.dispose()
      if (this.borderMesh.material instanceof THREE.MeshBasicNodeMaterial) {
        this.borderMesh.material.dispose()
      }
      this.borderMesh = undefined!
    }
    if (this.bgMesh) {
      this.bgMesh.geometry.dispose()
      if (this.bgMesh.material instanceof THREE.MeshBasicNodeMaterial) {
        this.bgMesh.material.dispose()
      }
      this.bgMesh = undefined!
    }
    if (this.group) {
      // Remove group from scene
      this.scene.remove(this.group)
      // Dispose geometries of any remaining meshes in the group
      this.group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
        }
      })
    }
  }

  resize() {
    this.createBounds()

    if (this.iconMesh) {
      this.positionIconMesh()
    } else {
      // Resize text mesh to update its bounds with current screen resolution
      this.textMesh?.resize()
      this.positionTextMesh()
    }

    this.positionCornersMesh()
    this.positionBorderMesh()

    // Dispose old geometry
    if (this.bgMesh) {
      this.bgMesh.geometry.dispose()
      this.bgMesh.geometry = new THREE.PlaneGeometry(
        this.bounds.width,
        this.bounds.height,
      )
    }
    this.proxyMesh.geometry.dispose()

    // Create new geometry with updated dimensions
    this.proxyMesh.geometry = new THREE.PlaneGeometry(
      this.bounds.width,
      this.bounds.height,
    )

    // Update cached rect when resizing
    this.updateCachedRect()
  }

  update() {
    if (!this.shouldUpdate) {
      return
    }

    const positionX =
      this.bounds.left + this.bounds.width / 2 - this.experience.sizes.width / 2
    const positionY =
      -this.y +
      this.experience.scrollInfo.animatedScroll +
      this.experience.sizes.height / 2 -
      this.bounds.height / 2

    this.proxyMesh.position.x = positionX
    this.proxyMesh.position.y = positionY

    this.group.position.x = positionX
    this.group.position.y = positionY

    // Update mouse circle position using pointer
    this.updateMouseCircle()
  }
}
